import { format, parseISO } from "date-fns"

/**
 * Remueve los acentos de una cadena.
 *
 * @param {string} str - La cadena a la cual se le quitarán los acentos.
 * @returns {string} La cadena sin acentos.
 */
const removeAccents = (str) => String(str).normalize('NFD').replace(/[\u0300-\u036f]/g, '')

/**
 * Filtra un array de objetos por una cadena de búsqueda.
 *
 * La búsqueda es sensible a mayúsculas y minúsculas. La función es recursiva,
 * es decir, busca en los objetos y arrays contenidos en los objetos.
 *
 * @param {Array} rows - El array de objetos a filtrar.
 * @param {string} search - La cadena de búsqueda.
 * @param {boolean} [deep=false] - Si se debe buscar recursivamente en objetos y arrays.
 * @returns {Array} El array de objetos filtrado.
 */
export const filterBySearch = (rows = [], search, deep = false) => {
  if (!search) return rows
  let words = removeAccents(search).toLowerCase().split(' ')

  // función recursiva que busca dentro de objetos y arrays
  const matchRow = (row, word) => {
    return Object.keys(row).some(key => {
      const value = row[key]

      if (value == null) return false

      // texto plano
      if (typeof value !== 'object') {
        return removeAccents(String(value)).toLowerCase().includes(word)
      }

      // si es un array
      if (Array.isArray(value)) {
        return deep && value.some(v =>
          typeof v === 'object' ? matchRow(v, word) : removeAccents(String(v)).toLowerCase().includes(word)
        )
      }

      // si es un objeto
      if (typeof value === 'object') {
        return deep && matchRow(value, word)
      }

      return false
    })
  }

  return rows.filter(row =>
    words.every(word => matchRow(row, word))
  )
}

/**
 * Filtra un array de objetos entre dos fechas.
 *
 * El array de objetos a filtrar se pasa como primer parámetro, el array de
 * claves que se quieren buscar como segundo parámetro, y el rango de fechas
 * como tercer parámetro.
 *
 * Se devuelve un nuevo array con los objetos que tienen una fecha en alguna
 * de las claves especificadas que se encuentra dentro del rango especificado.
 *
 * @param {Array} rows - El array de objetos a filtrar.
 * @param {string[]} keys - Las claves que se quieren buscar.
 * @param {[string|Date, string|Date]} range - Tupla con fecha inicial y final del rango.
 * @returns {Array} El array de objetos filtrado.
 */
export const filterBetweenDates = (rows = [], keys = [], [startDate, endDate]) => {
  const from = new Date(startDate).getTime();
  const to = new Date(endDate).getTime();

  return rows.filter(row =>
    keys.some(key => {
      if (!(key in row) || row[key] == null) return false;
      const date = new Date(format(parseISO(row[key]), 'yyyy-MM-dd')).getTime();
      return !isNaN(date) && date >= from && date <= to;
    })
  );
};

/**
 * Filtra una opcion de acuerdo a una cadena de busqueda.
 *
 * Si la cadena de busqueda es vacia, devuelve true.
 * Si no, separamos la cadena de busqueda en palabras y separamos el label de la
 * opcion en minusculas y sin acentos. Luego, se verifica si cada palabra
 * esta contenida en el label de la opcion.
 *
 * @param {string} input - La cadena de busqueda
 * @param {object} option - La opcion a filtrar
 * @returns {boolean} true si la opcion cumple con la busqueda, false de lo contrario
 */
export const filterOption = (input, option) => {
  if (!input) return true
  const words = removeAccents(input).toLowerCase().split(' ')
  const label = removeAccents(option.label).toLowerCase()
  return words.every(word => label.includes(word))
}